import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../utils/supabase';

const RoadSelectionApp = () => {
  const { wardSlug } = useParams();
  const [wardName, setWardName] = useState('');
  const [roads, setRoads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedRoad, setSelectedRoad] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setInitialLoading(true);
        
        // First get the ward information
        const { data: wardData, error: wardError } = await supabase
          .from('wards')
          .select('*')
          .eq('slug', wardSlug)
          .single();
        
        if (wardError) {
          throw wardError;
        }
        
        if (!wardData) {
          setErrorMessage('Ward not found.');
          return;
        }
        
        setWardName(wardData.name);
        
        // Then get the roads for this ward
        const { data: roadsData, error: roadsError } = await supabase
          .from('roads')
          .select('*')
          .eq('ward_id', wardData.id)
          .order('name');
        
        if (roadsError) {
          throw roadsError;
        }
        
        // Initialize roads with isResetting property
        const enhancedRoadsData = (roadsData || []).map(road => ({
          ...road,
          isResetting: false
        }));
        
        setRoads(enhancedRoadsData);
        
        // Set up real-time subscription for roads in this ward
        const newSubscription = supabase
          .channel('public:roads')
          .on('postgres_changes', { 
            event: '*', 
            schema: 'public', 
            table: 'roads',
            filter: `ward_id=eq.${wardData.id}`
          }, (payload) => {
            console.log('Change received!', payload);
            
            if (payload.eventType === 'INSERT') {
              // Add the new road with isResetting property
              setRoads(prevRoads => [...prevRoads, { ...payload.new, isResetting: false }]);
            } 
            else if (payload.eventType === 'UPDATE') {
              setRoads(prevRoads => 
                prevRoads.map(road => {
                  if (road.id === payload.new.id) {
                    // Preserve the isResetting state when updating from subscription
                    return { 
                      ...payload.new, 
                      isResetting: road.isResetting 
                    };
                  }
                  return road;
                })
              );
              
              // If the updated road was selected and is now unavailable, deselect it
              if (selectedRoad === payload.new.id && !payload.new.available) {
                setSelectedRoad(null);
                setErrorMessage('This road has just been selected by someone else. Please choose another road.');
                setTimeout(() => setErrorMessage(''), 5000);
              }
            } 
            else if (payload.eventType === 'DELETE') {
              setRoads(prevRoads => 
                prevRoads.filter(road => road.id !== payload.old.id)
              );
              
              // If the deleted road was selected, deselect it
              if (selectedRoad === payload.old.id) {
                setSelectedRoad(null);
              }
            }
          })
          .subscribe();
        
        // Clean up subscription when component unmounts
        return () => {
          if (newSubscription) {
            newSubscription.unsubscribe();
          }
        };
        
      } catch (error) {
        console.error('Error fetching data:', error);
        setErrorMessage('Failed to load ward data. Please try again.');
      } finally {
        setInitialLoading(false);
      }
    };
    
    fetchData();
  }, [wardSlug, selectedRoad]);
  
  const handleRoadSelect = (roadId) => {
    // Only allow selection if the road is available
    const road = roads.find(r => r.id === roadId);
    if (road && road.available) {
      setSelectedRoad(roadId);
      setSuccessMessage('');
      setErrorMessage('');
    } else {
      setErrorMessage('This road is no longer available. Please select another road.');
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const validateForm = () => {
    if (!formData.name.trim()) {
      setErrorMessage('Please enter your name');
      return false;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      setErrorMessage('Please enter a valid email address');
      return false;
    }
    if (!formData.phone.trim() || formData.phone.length < 10) {
      setErrorMessage('Please enter a valid phone number');
      return false;
    }
    return true;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedRoad) {
      setErrorMessage('Please select a road first');
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      // First check if the road is still available
      const { data: roadCheck, error: roadCheckError } = await supabase
        .from('roads')
        .select('available')
        .eq('id', selectedRoad)
        .single();
      
      if (roadCheckError) {
        throw roadCheckError;
      }
      
      if (!roadCheck.available) {
        setErrorMessage('This road was just claimed by someone else. Please select another road.');
        setSelectedRoad(null);
        return;
      }
      
      // Update the road in Supabase
      const { error: updateError } = await supabase
        .from('roads')
        .update({ 
          available: false,
          volunteer_name: formData.name,
          volunteer_email: formData.email,
          volunteer_phone: formData.phone 
        })
        .eq('id', selectedRoad);
      
      if (updateError) {
        throw updateError;
      }
      
      // Immediately update the UI to reflect the change
      setRoads(prevRoads => 
        prevRoads.map(road => 
          road.id === selectedRoad 
            ? { 
                ...road, 
                available: false,
                volunteer_name: formData.name,
                volunteer_email: formData.email,
                volunteer_phone: formData.phone 
              } 
            : road
        )
      );
      
      setSelectedRoad(null);
      setFormData({ name: '', email: '', phone: '' });
      setSuccessMessage('Thank you for volunteering! Your road selection has been confirmed.');
      setErrorMessage('');
      
    } catch (error) {
      console.error('Error updating road:', error);
      setErrorMessage('Failed to save your selection. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Render loading state
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center p-4">
        <div className="w-full max-w-4xl bg-white rounded-lg shadow-md p-6">
          <div className="flex flex-col items-center justify-center space-y-4">
            <svg className="animate-spin h-10 w-10 text-[#00bed6]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-600 text-lg">Loading road data...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="flex items-center mb-6">
        <Link to="/deliveryplanner" className="text-[#00bed6] hover:text-[#009eb3]">
          &larr; Back to Wards
        </Link>
      </div>
      
      <h1 className="text-3xl font-bold text-center mb-2 text-[#00bed6]">Leaflet Delivery Volunteer Sign-up</h1>
      <h2 className="text-xl text-center mb-8 text-[#201f20]">{wardName} Ward</h2>
      
      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded flex items-center justify-between">
          <span>{successMessage}</span>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-700 hover:text-green-900"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}
      
      {errorMessage && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded flex items-center justify-between">
          <span>{errorMessage}</span>
          <button 
            onClick={() => setErrorMessage('')}
            className="text-red-700 hover:text-red-900"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}
      
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <h2 className="text-xl font-semibold mb-4">Available Roads</h2>
          <div className="bg-white rounded-lg shadow-md p-4">
            {roads.length === 0 ? (
              <p className="text-center py-4 text-gray-600">No roads available in this ward.</p>
            ) : (
              <ul className="divide-y divide-gray-200">
                {roads.map(road => (
                  <li key={road.id} className="py-3">
                    <div className="flex items-center justify-between">
                      <span className={`font-medium ${!road.available ? 'text-gray-400' : ''}`}>
                        {road.name}
                        {road.houses > 0 && (
                          <span className="ml-2 text-sm text-gray-500">({road.houses} houses)</span>
                        )}
                      </span>
                      
                      {road.available ? (
                        <button
                          onClick={() => handleRoadSelect(road.id)}
                          className={`px-3 py-1 rounded text-sm ${
                            selectedRoad === road.id 
                              ? 'bg-[#00bed6] text-white' 
                              : 'bg-[#e6f7fa] text-[#00bed6] hover:bg-[#c5edf3]'
                          }`}
                        >
                          {selectedRoad === road.id ? 'Selected' : 'Select'}
                        </button>
                      ) : (
                        <div className="flex items-center">
                          <span className="text-sm text-green-600">Reserved</span>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        
        <div>
          <h2 className="text-xl font-semibold mb-4">Volunteer Information</h2>
          <div className="bg-white rounded-lg shadow-md p-4">
            {selectedRoad ? (
              <form onSubmit={handleSubmit}>
                <p className="mb-4">
                  You are signing up for: 
                  <span className="font-semibold ml-1">
                    {roads.find(r => r.id === selectedRoad)?.name}
                  </span>
                </p>
                
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                    disabled={loading}
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                    disabled={loading}
                  />
                </div>
                
                <div className="mb-6">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="phone">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                    disabled={loading}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <button
                    type="submit"
                    disabled={loading}
                    className={`bg-[#00bed6] hover:bg-[#009eb3] text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                  >
                    {loading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </span>
                    ) : 'Confirm Selection'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedRoad(null)}
                    className="text-[#00bed6] hover:text-[#009eb3]"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className="text-center py-8 text-gray-600">
                <p>Please select a road from the list to volunteer.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-4">Current Assignments</h2>
        <div className="bg-white rounded-lg shadow-md p-4">
          {roads.some(road => !road.available) ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Road</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Houses</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {roads.filter(road => !road.available).map(road => (
                    <tr key={road.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{road.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{road.houses || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                        <span className="text-green-600">Reserved</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center py-4 text-gray-600">No roads have been assigned yet in this ward.</p>
          )}
        </div>
      </div>
      
      <footer className="mt-12 text-center text-sm text-gray-500">
        <p className="mt-1">&copy; 2025 Reform UK - Welwyn Hatfield</p>
        <p className="mt-1">
          <Link to="/admin/login" className="text-gray-400 hover:text-gray-600 text-xs">Admin</Link>
        </p>
      </footer>
    </div>
  );
};

export default RoadSelectionApp;